import { useMutation, useQuery } from "@apollo/client";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import * as Yup from "yup";

import { FETCH_SHIPMENT as fetchShipment } from "../../../../graphql/query/Shipment";
import { FETCH_WAREHOUSE_MOVE_V2 as fetchWarehouseMoveV2 } from "../../../../graphql/query/WarehouseMove";
import AuthRoute from "../../../../components/AuthRoute";
import routes from "./routes";

import { NavBar, Spinner } from "../../../../components/ui";
import { Portal, getChangedFields } from "../../../../util";
import { useFormik } from "formik";
import { UPDATE_SHIPMENT as updateShipmentMutation } from "../../../../graphql/mutation/Shipment";
import { useEffect, useRef } from "react";

const Body = props => {
  const { loading, ...remainingProps } = props;

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...remainingProps} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const Wizard = props => {
  const { id } = useParams();
  const abortController = useRef();

  const [updateShipment] = useMutation(updateShipmentMutation(), {
    refetchQueries: ["FetchShipment"],
  });

  const { data: { shipment } = {}, loading } = useQuery(
    fetchShipment({
      // Associations
      category: true,
      consignee: {
        name: true,
        company: true,
        siteId: true,
      },
      consignor: { 
        company: true,
        name: true,
      },
      currency: true,
      delCountry: true,
      oriContact: {
        name: true,
        company: true,
        siteId: true,
      },
      oriCountry: true,
      shipper: {
        name: true,
        company: true,
      },
      warehouseMoves: true,
      // Scalar fields - Origin
      oriContactId: true,
      oriName: true,
      oriCompany: true,
      oriAddress: true,
      oriAddress2: true,
      oriAddress3: true,
      oriTown: true,
      oriCounty: true,
      oriCountryId: true,
      oriPostcode: true,
      // Scalar fields - Delivery
      delContactId: true,
      delName: true,
      delCompany: true,
      delAddress: true,
      delAddress2: true,
      delAddress3: true,
      delTown: true,
      delCounty: true,
      delCountryId: true,
      delPostcode: true,
      // Scalar fields - Customs
      customsCode: true,
      eori: true,
      customsCodeMatch: true,
      guaranteeAccountId: true,
      agent: true,
      customsRef: true,
      cds: true,
      declarant: true,
      bondRef: true,
      bondReleaseRef: true,
      customsDate: true,
      incoTerm: true,
      // Scalar fields - Documents
      customsRequest: true,
      customsRequestDate: true,
      customsRequestUser: true,
      customsReceived: true,
      customsReceivedDate: true,
      customsReceivedUser: true,
      customsCheck: true,
      customsCheckDate: true,
      customsCheckUser: true,
      // Scalar fields - Declarations
      customsDeclaration: true,
      instructions: true,
      condition: true,
      // Scalar fields - Insurance
      insurance: true,
      shipping: true,
      destination: true,
      examined: true,
      // Scalar fields - ShipmentDetail
      cancel: true,
      jobId: true,
      time: true,
      updatedAt: true,
      date: true,
      dispatched: true,
      delivered: true,
      currencyId: true,
      shipperId: true,
      shippingRef: true,
      categoryId: true,
      consignorId: true,
      consigneeId: true,
      itemCount: true,
      unconfirmedItemCount: true,
    }),
    {
      variables: { id: +id },
    },
  );

  const { data: { warehouseMoveV2: { edges = [] } = {} } = {} } = useQuery(
    fetchWarehouseMoveV2({
      site: true,
      siteId: true,
    }),
    {
      skip: !shipment?.id,
      variables: {
        input: {
          _shipment: {
            id: {
              operator: "eq",
              value: shipment?.id,
            },
          },
        },
      },
    },
  );

  const shipmentHasConfirmedItems =
    edges && edges.filter(edge => edge.siteId !== null)?.length > 0;

  const formik = useFormik({
    initialValues: shipment,
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      jobId: Yup.number().nullable().positive().integer(),
      time: Yup.number().nullable().positive().integer(),
    }),
    onSubmit: values => {
      const changedFields = getChangedFields(values, shipment);

      if (Object.keys(changedFields).length > 0) {
        const controller = new AbortController();
        abortController.current = controller;
        updateShipment({
          variables: {
            input: {
              id: shipment?.id,
              ...changedFields,
            },
          },
          context: {
            fetchOptions: {
              signal: controller.signal,
            },
          },
        });
      }
    },
  });

  useEffect(() => {
    if (formik?.values?.delivered === "") {
      formik.setFieldValue("delivered", null);
    }
    if (formik?.values?.date === "") {
      formik.setFieldValue("date", null);
    }
  }, [formik?.values]);

  useEffect(() => {
    if (formik?.values && abortController.current) {
      abortController.current.abort();
    }
  }, [formik?.values]);

  const bodyProps = {
    ...props,
    formik,
    shipment,
    shipmentId: +id,
    loading,
    shipmentHasConfirmedItems,
  };
      
  return (
    <div className="relative flex min-h-full">
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="flex flex-1 overflow-x-auto">
        <Body {...bodyProps} loading={loading} />
      </div>
    </div>
  );
};

export default Wizard;
